// REACT_APP_API_ORIGIN is inlined at CRA build time and REQUIRED for every
// build, staging and production alike (TDD_107a rev 3). There is deliberately
// no fallback: a bundle whose origin was never stated must fail on first paint,
// never silently call production — staging UAT reading prod athlete data is the
// exact defect this closes. The prebuild check in package.json catches the
// unset case before a bundle even exists; this allowlist is the runtime guard.
// Allowlisted as host tokens, assembled at validation time — never as full-URL
// literals. The UAT hard gate greps the built bundle for full API origins and
// must find EXACTLY ONE (the CRA-inlined env value); a full-URL allowlist here
// would plant the other environment's origin in every bundle and break that
// check.
const ALLOWED_API_HOSTS = ['fitmagic-production', 'fitmagic-staging'];

function resolveOrigin(raw) {
  const ok = !!raw && ALLOWED_API_HOSTS.some(h => raw === 'https://' + h + '.up.railway.app');
  if (!ok) {
    throw new Error(
      `REACT_APP_API_ORIGIN is ${raw ? `"${raw}"` : 'unset'} — it must be set at build time ` +
      'to the staging or production FitMagic API origin. Refusing to guess an API origin.'
    );
  }
  return raw;
}

export const ORIGIN = resolveOrigin(process.env.REACT_APP_API_ORIGIN);

// Typed error so callers can branch on status (401 re-auth, 403 consent
// revoked, 404 stale resource) instead of parsing a message string. Message
// keeps the legacy `${status}: ${body}` shape, so pre-existing call sites that
// only read .message behave exactly as before.
export class ApiError extends Error {
  constructor(status, detail, url) {
    super(`${status}: ${detail}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.url = url;
  }
}

const SESSION_KEY = 'fitmagic_builder_token';

function getToken() {
  try {
    const urlToken = new URLSearchParams(window.location.search).get('token');
    if (urlToken) {
      // Persist to sessionStorage then strip from URL so it doesn't leak via
      // copy/paste, browser history, or referrer headers.
      sessionStorage.setItem(SESSION_KEY, urlToken);
      const clean = new URL(window.location.href);
      clean.searchParams.delete('token');
      window.history.replaceState(null, '', clean.toString());
    }
    return sessionStorage.getItem(SESSION_KEY) || null;
  } catch {
    return null;
  }
}

export function setSessionToken(token) {
  try { sessionStorage.setItem(SESSION_KEY, token); } catch {}
}

export function clearSessionToken() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch {}
}

async function request(path, options = {}, prefix = '/api') {
  const token = getToken();
  const url = `${ORIGIN}${prefix}${path}`;
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) throw new ApiError(res.status, await res.text(), url);
  const text = await res.text();
  if (!text) return {};
  try { return JSON.parse(text); } catch { return {}; }
}

export const register = async (email, password, first_name, last_name = '', referral_code = '', date_of_birth = '') => {
  const body = { email, password, first_name };
  if (last_name) body.last_name = last_name;
  if (referral_code) body.referral_code = referral_code;
  if (date_of_birth) body.date_of_birth = date_of_birth;
  const res = await fetch(`${ORIGIN}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = 'Registration failed';
    try { msg = JSON.parse(text).detail || msg; } catch {}
    throw new Error(msg);
  }
  const data = await res.json();
  setSessionToken(data.token);
  return data;
};

export const forgotPassword = async (email) => {
  const res = await fetch(`${ORIGIN}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Could not send reset email');
  return res.json();
};

export const resetPassword = async (token, new_password) => {
  const res = await fetch(`${ORIGIN}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password }),
  });
  if (!res.ok) throw new Error('Password reset failed');
  return res.json();
};

export const login = async (email, password) => {
  const res = await fetch(`${ORIGIN}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.text();
    let msg = 'Login failed';
    try { msg = JSON.parse(body).detail || msg; } catch {}
    throw new Error(msg);
  }
  const data = await res.json();
  setSessionToken(data.token);
  return data;
};

export const getMe = () => request('/me', {}, '/auth');
export const searchExercises = (q) => request(`/exercises/search?q=${encodeURIComponent(q)}`);
export const listExercises = () => request('/exercises');
export const getBodyParts = () => request('/body-parts');
export const getPrograms = (pid) => request(`/programs/${pid}`);
export const createProgram = (pid, name, split, days) =>
  request(`/programs?profile_id=${pid}&program_name=${encodeURIComponent(name)}&split_type=${encodeURIComponent(split || '')}&days_per_week=${days || 0}`, { method: 'POST' });
export const getTemplates = (pid) => request(`/templates/${pid}`);
export const getTemplateDetail = (pid, tid) => request(`/templates/${pid}/detail/${tid}`);
export const createTemplate = (programId, name, type) =>
  request(`/templates?program_id=${programId}&name=${encodeURIComponent(name)}&workout_type=${encodeURIComponent(type)}`, { method: 'POST' });
export const saveTemplateSets = (tid, sets) =>
  request(`/templates/${tid}/sets`, { method: 'PUT', body: JSON.stringify(sets) });
export const deleteTemplate = (tid) =>
  request(`/templates/${tid}`, { method: 'DELETE' });
export const createExercise = (name, weightType = 'Body Weight', muscleGroup = null) =>
  request(`/exercises/create?name=${encodeURIComponent(name)}&weight_type=${encodeURIComponent(weightType)}${muscleGroup ? `&muscle_group=${encodeURIComponent(muscleGroup)}` : ''}`, { method: 'POST' });
export const updateProgram = (pid, { name, description, splitType, daysPerWeek, goal, parentProgramId } = {}) => {
  const params = new URLSearchParams();
  if (name) params.set('name', name);
  if (description !== undefined) params.set('description', description);
  if (splitType !== undefined) params.set('split_type', splitType);
  if (daysPerWeek !== undefined) params.set('days_per_week', daysPerWeek);
  if (goal) params.set('goal', goal);
  if (parentProgramId !== undefined) params.set('parent_program_id', parentProgramId || 0);
  return request(`/programs/${pid}?${params}`, { method: 'PATCH' });
};
export const deleteProgram = (pid, { keepTemplates } = {}) => {
  const qs = keepTemplates ? '?keep_templates=true' : '';
  return request(`/programs/${pid}${qs}`, { method: 'DELETE' });
};
export const updateTemplate = (tid, { name, programId, workoutType } = {}) => {
  const params = new URLSearchParams();
  if (name) params.set('name', name);
  if (programId !== undefined && programId !== null) params.set('program_id', programId); // 0 = clear program
  if (workoutType) params.set('workout_type', workoutType);
  return request(`/templates/${tid}?${params}`, { method: 'PATCH' });
};
export const getWorkoutGroups = () => request('/picklists/workout-groups');
export const getAnalytics = (pid, period = 'week') => request(`/analytics/${pid}?period=${period}`);
export const getCaloriePattern = (pid, weeks = 5) => request(`/analytics/calorie-pattern/${pid}?weeks=${weeks}`);

// Coach analytics (TDD_107a). Caller identity is the JWT server-side — the
// athlete ids below are TARGETS from roster data, never the caller. All auth is
// the Bearer header via request(); no token ever appears in these URLs.
// limit default 200 = the full-fetch page size: the client pages to completion
// and holds the whole roster (TDD_107a contract fact 10).
export const getCoachRoster = ({ localDate, sort = 'name', direction = 'asc', limit = 200, offset = 0 }) =>
  request(`/coach/roster?local_date=${localDate}&sort=${sort}&direction=${direction}&limit=${limit}&offset=${offset}`);
export const getStudentWorkouts         = (id) => request(`/coach/students/${id}/workouts`);
export const getStudentWorkoutSets      = (id, workoutId) => request(`/coach/students/${id}/workouts/${workoutId}/sets`);
export const getStudentMuscleRecovery   = (id) => request(`/coach/students/${id}/muscle-recovery`);
export const getStudentNutritionSummary = (id) => request(`/coach/students/${id}/nutrition-summary`);

// Admin
export const listAllProfiles = () => request('/admin/profiles');
export const listAllTemplates = () => request('/admin/templates/all');
export const assignTemplate = (templateId, targetProfileId) =>
  request(`/admin/templates/assign?template_id=${templateId}&target_profile_id=${targetProfileId}`, { method: 'POST' });
export const getWorkoutTypes = () => request('/picklists/workout-types');
export const getSetTypes = () => request('/picklists/set-types');
export const getBandColors = () => request('/picklists/band-colors');
